import { HTTPError, Logger } from '@gitbook/util-debug';
import { assert, assertNever } from '@gitbook/util-ts';

import {
    Api,
    type Error as ApiError,
    type FullRequestParams,
    type HttpResponse,
    type IntegrationBlock,
    type QueryParamsType,
} from './__generated__';

export * from './__generated__';
export * from './__generated__/constants';
export * from './channels';
export * from './computed';
export * from './oauth-scopes';
export * from './operations';
export * from './paths';
export * from './subscriptions';
export * from './types';
export * from './visitor-bots';

export interface IntegrationsBlocksSet {
    [integrationKey: string]: IntegrationBlock[];
}

interface SecurityData {
    /**
     * Castle.io request token to append to the requests for an extra layer of security.
     */
    getCastleToken: () => Promise<string>;

    /**
     * Authentication token to use.
     */
    getAuthToken: () => Promise<string | undefined>;
}

const SESSION_AFFINITY_HEADER = 'x-gitbook-session-affinity';
const CASTLE_REQUEST_TOKEN_HEADER = 'X-Castle-Request-Token';
const logger = Logger('api-client');

/*
 * Export the auto-generated API client under the name 'GitBookAPI'
 * and export all API types.
 */
export class GitBookAPI extends Api<SecurityData> {
    /**
     * Session affinity to use during the session.
     * We currently do not persist between sessions, but we might in the future.
     */
    private sessionAffinity: string | undefined;

    constructor(
        options: SecurityData & {
            /**
             * API endpoint to use.
             */
            endpoint: string;

            /** Refresh the cached ID token after authentication rejects it. */
            refreshAuthToken?: () => Promise<string | undefined>;

            /**
             * Minimum criticity level accepted by the API.
             * - If set to "high", all requests that are not critical will returns an error.
             * - If set to "low", all requests will be accepted.
             * This parameter is used with "selective" API test mode.
             * @default "low"
             */
            criticalityLevel?: 'low' | 'high';

            /**
             * Handle an API response.
             */
            onResponse?: (
                params: FullRequestParams,
                response: HttpResponse<any, any>
            ) => void | Promise<void>;
        }
    ) {
        const {
            endpoint,
            getAuthToken,
            getCastleToken,
            onResponse,
            criticalityLevel,
            refreshAuthToken,
        } = options;

        super({
            baseUrl: `${endpoint}/v1`,
            customFetch: async (input, init) => {
                // The API only checks Castle tokens on writes, and creating one is slow.
                const method = (init?.method ?? 'GET').toUpperCase();
                if (method === 'GET' || method === 'HEAD') {
                    return fetch(input, init);
                }

                return fetch(input, {
                    ...init,
                    headers: {
                        ...init?.headers,
                        [CASTLE_REQUEST_TOKEN_HEADER]: await getCastleToken(),
                    },
                });
            },
            securityWorker: async (securityData, mode) => {
                assert(securityData, 'Security data is required');

                const authToken = await securityData.getAuthToken();

                const query: QueryParamsType = {};
                const headers: Record<string, string> = {};

                if (authToken) {
                    switch (mode) {
                        case 'request': {
                            headers.Authorization = `InternalToken ${authToken}`;
                            break;
                        }
                        case 'streaming': {
                            query.authIdToken = authToken;
                            break;
                        }
                        default:
                            assertNever(mode);
                    }
                }

                if (this.sessionAffinity) {
                    headers[SESSION_AFFINITY_HEADER] = this.sessionAffinity;
                }

                return {
                    query,
                    headers,
                };
            },
        });

        this.setSecurityData({ getAuthToken, getCastleToken });

        if (onResponse || criticalityLevel || refreshAuthToken) {
            const superRequest = this.request;
            this.request = async <T, E extends ApiError = ApiError>(
                params: FullRequestParams
            ): Promise<HttpResponse<T, E>> => {
                const overridedParams = {
                    ...params,
                    ...(criticalityLevel
                        ? {
                              headers: {
                                  ...params.headers,
                                  'x-gitbook-criticality': criticalityLevel,
                              },
                          }
                        : {}),
                };

                let response: HttpResponse<T, E>;
                try {
                    response = await superRequest<T, E>(overridedParams);
                } catch (error) {
                    if (
                        !params.secure ||
                        params.cancelToken !== undefined ||
                        !refreshAuthToken ||
                        !(error instanceof HTTPError) ||
                        error.statusCode !== 401 ||
                        error.message !== 'Invalid ID token authentication'
                    ) {
                        throw error;
                    }

                    const token = await refreshAuthToken();
                    if (!token) {
                        throw error;
                    }
                    response = await superRequest<T, E>(overridedParams);
                }

                const upstreamSessionAffinity = response.headers.get(SESSION_AFFINITY_HEADER);
                if (upstreamSessionAffinity) {
                    if (!this.sessionAffinity) {
                        logger.info('upstream session affinity found', { upstreamSessionAffinity });
                    }
                    this.sessionAffinity = upstreamSessionAffinity;
                }

                await onResponse?.(overridedParams, response);

                return response;
            };
        }
    }
}
